// @ts-ignore esModuleInterop
import React, { useCallback, useMemo, useState } from 'react'
import * as blueprint from '@adc/blueprint'
import type {
  AuctionBlueprintOnlineReactiveArgs,
  FlowDefinition,
  primitives,
} from '@adc/blueprint'
import { Button, Stepper } from '@adc/ui-adc'
import _ from 'lodash'
import _cx from 'classnames/bind'
import type { Property } from '@adc/sdk/types'
import * as graph from '@adc/sdk'
// @ts-ignore no types
import instrument from '@adc/instrument'
// @ts-ignore no types
import cls from './styles.scss'
import BasicInformationStep from './basicInformationStep'
import AcknowledgementsAndAgreementStep from './acknowledgementsAndAgreementStep'
import CompleteStep from './completeStep'
import { saveFirstLookBuyerSelection } from '../../providers/firstLook'
import { useListingContext } from 'common/context/listing'
import analytics from '../../services/analytics'

const cx = _cx.bind(cls)

export type StepId = 'basic_information' | 'acknowledgements' | 'complete'

export type FirstLookFlowData = {
  fullName: string
  selection: string
  __computed__?: {
    errorResponse?: boolean
  }
}

export interface Context {
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  isCaliforniaSpecialTreatmentRequired: boolean
  isInvestorSelection: boolean
  isMobile: boolean
  user: primitives.AuctionUser
  size?: 'large' | 'small' | 'medium' | 'marshmallow'
  closeModal: () => void
  handleRegistration: () => void
}

export const firstLookFlowDefinition: FlowDefinition<
  StepId,
  FirstLookFlowData,
  Context
> = [
  {
    id: 'basic_information',
    next: ({ context }) =>
      !context?.isInvestorSelection ? 'acknowledgements' : 'complete',
    isNextStepEnabled: ({ data }) => !!data?.selection,
    component: BasicInformationStep,
    touched: false,
    isLastStep: () => false,
    label: 'Basic Information',
  },
  {
    id: 'acknowledgements',
    next: () => 'complete',
    isNextStepEnabled: ({ data }) => Boolean(data?.fullName),
    canBeSkipped: ({ context }) => Boolean(context?.isInvestorSelection),
    component: AcknowledgementsAndAgreementStep,
    touched: false,
    isLastStep: () => false,
    label: 'Acknowledgements',
  },
  {
    id: 'complete',
    next: () => 'complete',
    isNextStepEnabled: () => true,
    component: CompleteStep,
    touched: false,
    isLastStep: () => true,
    label: 'Complete',
    completed: () => false,
  },
]

interface FirstLookFlowProps {
  elmId?: string
  isMobile: boolean
  user: primitives.AuctionUser
  featureFlags: Record<string, unknown>
  property: Property
  size?: 'large' | 'small' | 'medium' | 'marshmallow'
  onSaveFirstLookBuyerSelection: (
    isCurrentUserFirstLookQualified: boolean
  ) => void
  handleRegistration: () => void
  closeModal: () => void
}

const FirstLookFlow = ({
  featureFlags,
  isMobile,
  property,
  size,
  user,
  handleRegistration,
  closeModal,
  onSaveFirstLookBuyerSelection,
}: FirstLookFlowProps) => {
  const { listing, listingActions } = useListingContext()
  const [firstLookData, setFirstLookData] = useState<FirstLookFlowData>({
    fullName: '',
  } as FirstLookFlowData)

  const context: Context = useMemo(() => {
    const address = property?.summary?.address || {}
    const propertyState = address?.country_primary_subdivision
    const isCaliforniaSpecialTreatmentRequired = Boolean(
      featureFlags['fe.resi.firstlook-ca-treatment-required'] &&
        propertyState === 'CA'
    )
    return {
      isCaliforniaSpecialTreatmentRequired,
      isInvestorSelection:
        firstLookData?.selection ===
        graph.enums.FirstLookBuyerSelectionType.Investor,
      isMobile,
      listing,
      user,
      size,
      closeModal,
      handleRegistration,
    }
  }, [
    user,
    firstLookData,
    property,
    featureFlags,
    isMobile,
    size,
    handleRegistration,
    closeModal,
  ])

  const [loading, setLoading] = useState(false)

  const handleSubmitInvestor = useCallback(async () => {
    const { selection } = firstLookData
    Boolean(selection) && analytics.firstLookModal.done(selection)
    closeModal()
  }, [firstLookData, closeModal])

  const handleSubmitAcknowledgements = useCallback(async () => {
    const { selection, fullName } = firstLookData

    setLoading(true)
    // if we're not an investor and going next on second step, submit API call saving buyer selection
    const saveFirstLookSelectionPayload = {
      listing_id: listing?.listing_id || '',
      signature: fullName || '',
      buyer_selection: selection,
    }
    try {
      const res = await saveFirstLookBuyerSelection(
        saveFirstLookSelectionPayload
      )
      if (!listing?.is_current_user_first_look_qualified) {
        listingActions?.refresh(listing)
        // TODO: it should go through blueprint refresh mechanism in future
        onSaveFirstLookBuyerSelection(true)
      }

      setLoading(false)
      setFirstLookData({
        ...firstLookData,
        __computed__: {
          errorResponse: Boolean(res.body?.errors),
        },
      })

      instrument.increment('first_look_buyer_selection', 1, [
        `status:${res.body.errors ? 'failure' : 'success'}`,
      ])

      Boolean(selection) && analytics.firstLookModal.complete(selection)
    } catch (_e) {
      setLoading(false)
      setFirstLookData({
        ...firstLookData,
        __computed__: {
          errorResponse: true,
        },
      })
    }
  }, [
    listing,
    setLoading,
    firstLookData,
    setFirstLookData,
    listingActions,
    context,
    onSaveFirstLookBuyerSelection,
  ])

  const memoizedStepsDefinition = useMemo(
    () =>
      firstLookFlowDefinition.filter(
        step => !step?.canBeSkipped?.({ context })
      ),
    [context]
  )

  const emitter = useMemo(() => {
    return {
      on: (event: string, payload: any) => {
        if (event === 'flow:complete') {
          instrument.domainEvent({
            type: graph.enums.DomainEventTypeName.FlowComplete,
            source: listing.urn as string,
            data: payload,
          })
        } else if (event === 'flow:advance') {
          instrument.domainEvent({
            type: graph.enums.DomainEventTypeName.FlowAdvance,
            source: listing.urn as string,
            data: payload,
          })
        } else if (event === 'flow:retreat') {
          instrument.domainEvent({
            type: graph.enums.DomainEventTypeName.FlowRetreat,
            source: listing.urn as string,
            data: payload,
          })
        }
      },
    }
  }, [listing])

  const { data, actions } = blueprint.flows.hooks.useFlow<
    StepId,
    FirstLookFlowData,
    Context
  >({
    definition: memoizedStepsDefinition,
    context,
    emitter,
  })

  const {
    currentProgress,
    currentStepTouched,
    currentStepObject,
    stepsVisited,
  } = data
  const { setCurrentStepTouched, retreat, advance } = actions

  const ComponentToRender = data.currentStepObject.component

  const isFirstStep = currentProgress === 0

  const progressedBeyondStep = useCallback(
    (stepId: StepId) => {
      return currentStepObject.id !== stepId && stepsVisited.includes(stepId)
    },
    [stepsVisited, currentStepObject]
  )

  // we need this structured array of object to power the Stepper component
  const stepperSteps = memoizedStepsDefinition.map(step => {
    return {
      elmId: step.id,
      stepItem: step.label || '',
      // if we have a completed function associated with the step, we call it to determine if the step is completed
      // otherwise we check if the step has been progressed beyond - only for visual indication of completion on the stepper
      completed: step?.completed
        ? step.completed({
            data: firstLookData,
            context,
          })
        : progressedBeyondStep(step.id),
    }
  })

  const currentStepIndex = stepperSteps.findIndex(
    s => s.elmId === currentStepObject.id
  )

  const isCompleted = currentStepObject.id === 'complete'

  const handleNext = useCallback(async () => {
    if (
      currentStepObject.isLastStep({ context, data: firstLookData }) &&
      context.isInvestorSelection
    ) {
      await handleSubmitInvestor()
      return
    } else if (currentStepObject.id === 'acknowledgements') {
      await handleSubmitAcknowledgements()
    }
    setCurrentStepTouched(false)
    advance({ data: firstLookData, setData: setFirstLookData })
  }, [
    currentStepObject.id,
    context,
    handleSubmitInvestor,
    handleSubmitAcknowledgements,
    setCurrentStepTouched,
    advance,
    firstLookData,
    setFirstLookData,
  ])

  const handleCancel = useCallback(() => {
    instrument.domainEvent({
      type: graph.enums.DomainEventTypeName.FlowAbandon,
      source: listing.urn as string,
      data: {
        from: currentStepObject.id,
      },
    })
    closeModal()
  }, [closeModal, currentStepObject, listing])

  const handleStepperNavigation = useCallback(
    (
      _e: React.MouseEvent<HTMLButtonElement>,
      data: { completed: boolean; stepItem: string; activeStep: number }
    ) => {
      if (isCompleted || data.activeStep >= currentStepIndex) return
      retreat()
    },
    [retreat, currentStepIndex, isCompleted]
  )

  return (
    <div className={cx('flow-container')}>
      <Stepper
        steps={stepperSteps}
        activeStep={currentStepIndex}
        elmId='stepper'
        className={cx('stepper', { completed: isCompleted })}
        size='medium'
        onStepClick={handleStepperNavigation}
        onClose={handleCancel}
      />
      <div className={cx('content-wrapper')}>
        <div className={cx('main-content-area')}>
          <ComponentToRender
            data={firstLookData}
            setData={setFirstLookData}
            progress={currentProgress}
            touched={currentStepTouched}
            setTouched={setCurrentStepTouched}
            context={context}
          />
        </div>
        {currentStepIndex < 2 && (
          <div
            className={cx(
              'button-wrapper',
              !isMobile ? (isFirstStep ? 'u-mt-2' : 'u-mt-3') : ''
            )}
          >
            {(!isMobile || isFirstStep) && (
              <a href='/firstlook' target='_blank' rel='noreferrer'>
                First Look FAQs
              </a>
            )}
            {!isMobile && (
              <Button
                role='secondary'
                elmId={`${isFirstStep ? 'cancel' : 'back'}_button`}
                onClick={isFirstStep ? handleCancel : retreat}
              >
                {isFirstStep ? 'Cancel' : 'Back'}
              </Button>
            )}
            <Button
              role='primary'
              elmId='confirm_button'
              onClick={handleNext}
              loading={loading}
              disabled={
                !currentStepObject.isNextStepEnabled({
                  data: firstLookData,
                  setData: setFirstLookData,
                  context,
                })
              }
            >
              {firstLookData.selection ===
                graph.enums.FirstLookBuyerSelectionType.Investor &&
              currentStepIndex === 1
                ? 'Stay Updated'
                : isMobile
                ? 'Continue'
                : 'Next'}
            </Button>
            {isMobile &&
              firstLookData.selection ===
                graph.enums.FirstLookBuyerSelectionType.Investor &&
              currentStepIndex === 1 && (
                <Button
                  role='secondary'
                  elmId={`close_button`}
                  onClick={closeModal}
                >
                  {'Close'}
                </Button>
              )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FirstLookFlow
